import { agentSessionApi, type AgentSessionId } from "@/api/agentSessions";
import type { TicketId } from "@/api/tickets";
import { defineQuery, defineQueryOptions, useQuery, useQueryCache } from "@pinia/colada";
import { computed } from "vue";
import { TICKET_KEYS } from "./tickets";

// Under the tickets root so invalidateTicketData() refetches after a status change clears a mark.
export const AGENT_WORK_KEYS = {
    root: [...TICKET_KEYS.root, "agentWork"] as const,
    mine: () => [...AGENT_WORK_KEYS.root, "mine"] as const,
    ticket: (id: TicketId) => [...AGENT_WORK_KEYS.root, "ticket", id] as const,
};

export const useMyAgentWork = defineQuery(() => {
    const query = useQuery({
        key: AGENT_WORK_KEYS.mine(),
        query: () => agentSessionApi.listMine(),
    });
    return { ...query, work: computed(() => query.state.value.data ?? []) };
});

export const ticketAgentWorkQuery = defineQueryOptions((ticketId: TicketId) => ({
    key: AGENT_WORK_KEYS.ticket(ticketId),
    query: () => agentSessionApi.forTicket(ticketId),
}));

/** Pushed by the server whenever an agent's mark on the ticket changes. */
export function onAgentWorkChanged(ticketId: TicketId) {
    const cache = useQueryCache();
    void cache.invalidateQueries({ key: AGENT_WORK_KEYS.mine() });
    void cache.invalidateQueries({ key: AGENT_WORK_KEYS.ticket(ticketId) });
}

export async function clearStaleAgentWork() {
    await agentSessionApi.clearStale();
    await useQueryCache().invalidateQueries({ key: AGENT_WORK_KEYS.root });
}

export async function releaseAgentWork(session: AgentSessionId, ticketId: TicketId) {
    await agentSessionApi.release(session, ticketId);
    const cache = useQueryCache();
    cache.setQueryData(AGENT_WORK_KEYS.ticket(ticketId), null);
    await cache.invalidateQueries({ key: AGENT_WORK_KEYS.mine() });
}
